import { get } from 'svelte/store';

import { SLEEPING_TYPES } from '../constants';
import { get_local_ip, is_gen_z, real_id, getZWaveChannelIndex } from '../helpers/interface_helpers';
import {
	findZWaveGatewayForDevice,
	resolveZWaveDeviceComponentId,
} from '../helpers/z_wave_gateway_helpers';
import { resolveZWaveComponentKey } from '../helpers/z_wave_status_helpers';
import {
	get_fresh_verified_local_ip,
	refresh_verified_local_ip,
} from '../helpers/local_ip_probe_helpers';
import { gen2Request } from './gen_2_http_reqs';
import { socket_send } from './socket_commands';
import { localReq } from './http';
import { tryApplyMockLightCommand } from 'src/helpers/dev/mock_light_commands';
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';

import type { Request } from '../types/requests/request';
import type { Device } from 'src/types/Device';

/**
 * Cloud RPC for Z-Wave end devices — same shape as Jrpc, event Shelly:XZRequest.
 */
export const xzCloudReq = async (
	id: string,
	type: string,
	method: string,
	params: {},
	viaq?: boolean,
	timeout?: number,
) => {
	let res;

	if (typeof viaq === 'undefined' && SLEEPING_TYPES.includes(type)) {
		viaq = true;
	}

	try {
		res = await socket_send(id, params, 'Shelly:XZRequest', method, viaq, timeout);
	} catch (error) {
		res = { error: error.message };
	} finally {
		if (!res) {
			return { isok: false };
		}
		if (res?.hasOwnProperty('result')) {
			res = { isok: true, data: res.result };
		}
		if (!res?.hasOwnProperty('isok')) {
			res = {
				isok: !res?.hasOwnProperty('error'),
				...res,
			};
		}
	}

	return res;
};

/**
 * Device request for Gen Z / ZW-* — local rpc when available, otherwise Shelly:XZRequest.
 * Gateway Gen2 `Zwave.*` inclusion RPCs should keep using {@link gen2Request}.
 */
export const xzRequest = async (
	device: Device,
	method: string,
	params: {},
	viaq?: boolean,
	skip_local: boolean = false,
	timeout?: number,
) => {
	const mockResult = tryApplyMockLightCommand(device, method, params as Record<string, unknown>);
	if (mockResult) return mockResult;

	const local_ip = get_local_ip(real_id(device.id));

	let res: Request;
	if (local_ip && !skip_local) {
		res = await localReq(device, `${local_ip}/rpc/`, 'post', {
			id: Date.now(),
			method,
			params,
			src: 'ShellyApp',
		});

		if (!res?.isok) {
			const cachedIp = get_fresh_verified_local_ip(real_id(device.id));
			if (cachedIp && cachedIp !== local_ip) {
				res = await localReq(device, `${cachedIp}/rpc/`, 'post', {
					id: Date.now(),
					method,
					params,
					src: 'ShellyApp',
				});
			}
			if (!res?.isok) {
				void refresh_verified_local_ip(device.id, { debounced: true });
			}
		}
	} else {
		// Socket host lookup needs channel suffix for channel > 0; payload deviceId is still stripped in socket_send.
		const socketId =
			(device.channel ?? 0) === 0
				? real_id(device.id)
				: device.id.includes('_')
					? device.id
					: `${real_id(device.id)}_${device.channel}`;
		res = await xzCloudReq(socketId, device.type, method, params, viaq, timeout);
	}

	if (!res?.isok && local_ip && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
		const socketId =
			(device.channel ?? 0) === 0
				? real_id(device.id)
				: device.id.includes('_')
					? device.id
					: `${real_id(device.id)}_${device.channel}`;
		res = await xzCloudReq(socketId, device.type, method, params, viaq, timeout);
	}
	return res;
};

/** Prefer XZ for Gen Z end devices; Gen2 gateways stay on Jrpc. */
export const isXzDevice = (device: Device | undefined): boolean => {
	if (!device) return false;
	return is_gen_z(device) || device.type?.startsWith('ZW-') || device.id?.startsWith('XZ');
};

/** Map legacy Z-Wave command-class names to Gen2 component RPC methods (`Switch.Set`). */
const ZWAVE_CC_TO_GEN2_COMPONENT: Record<string, string> = {
	BinarySwitch: 'Switch',
	'Binary Switch': 'Switch',
	MultilevelSwitch: 'Cover',
	'Multilevel Switch': 'Cover',
	Configuration: 'Configuration',
};

export const toXzMethod = (commandClass: string, method: string): string => {
	const normalizedCc = commandClass.trim();
	const component =
		ZWAVE_CC_TO_GEN2_COMPONENT[normalizedCc] ||
		ZWAVE_CC_TO_GEN2_COMPONENT[normalizedCc.replace(/\s+/g, '')] ||
		normalizedCc.replace(/\s+/g, '');
	const m = method.trim();
	return `${component}.${m.charAt(0).toUpperCase()}${m.slice(1).toLowerCase()}`;
};

export type ZwaveCallPayload = {
	cc: string;
	m: string;
	ep?: number;
	params?: string | unknown;
};

const parseZwaveCallParams = (params: ZwaveCallPayload['params']): unknown => {
	if (typeof params !== 'string') {
		return params;
	}
	try {
		return JSON.parse(params);
	} catch {
		return params;
	}
};

const buildXzParams = (payload: ZwaveCallPayload): Record<string, unknown> => {
	const callParams = parseZwaveCallParams(payload.params);
	const ep = payload.ep ?? 0;
	const method = payload.m.toLowerCase();

	if (payload.cc === 'Configuration' && method === 'set' && Array.isArray(callParams)) {
		const first = callParams[0] as { parameter?: number; value?: number } | undefined;
		return { id: ep, parameter: first?.parameter, value: first?.value };
	}

	if (payload.cc === 'Configuration' && method === 'get' && Array.isArray(callParams)) {
		return { id: ep, parameter: callParams[0] };
	}

	if (callParams && typeof callParams === 'object' && !Array.isArray(callParams)) {
		return { id: ep, ...(callParams as Record<string, unknown>) };
	}

	if (callParams !== undefined) {
		return { id: ep, args: callParams };
	}

	return { id: ep };
};

/**
 * End-device Z-Wave call via Shelly:XZRequest — same as Jrpc: `method` + `params` only.
 * Prefer this over legacy `/device/zw` for end-device actions (delete uses `ZWaveDevice.FactoryReset`).
 */
export const zwaveCall = async (device: Device, payload: ZwaveCallPayload, timeout?: number) => {
	return xzRequest(
		device,
		toXzMethod(payload.cc, payload.m),
		buildXzParams(payload),
		undefined,
		false,
		timeout,
	);
};

/**
 * Component id inside XZ request params for Switch/Light/Cover (not the deviceId).
 * Prefer live status (`light:100` → 100), then 100 + channel.
 * Do not use `settings.device.id` — that is the gateway `zwavedevice` id (200+).
 */
export const getXzComponentId = (device: Device): number => {
	const status = devices_status.getByRealId(device.id) as Record<string, any> | undefined;
	if (status) {
		const key = resolveZWaveComponentKey(device, status);
		const fromComponent = Number(status?.[key]?.id);
		if (Number.isFinite(fromComponent) && fromComponent < 200) {
			return fromComponent;
		}
		const fromKey = Number(String(key).split(':').pop());
		if (Number.isFinite(fromKey) && fromKey < 200) {
			return fromKey;
		}
	}

	return 100 + getZWaveChannelIndex(device);
};

/** Gen2-style on/off: `Switch.Set` + `{ id, on }`. */
export const zwaveBinarySwitchSet = (device: Device, value: boolean) => {
	return xzRequest(device, 'Switch.Set', {
		id: getXzComponentId(device),
		on: value,
	});
};

/** Gen2-style cover position: `Cover.GoToPosition` + `{ id, pos }`. */
export const zwaveMultilevelSwitchSet = (device: Device, value: number) => {
	return xzRequest(device, 'Cover.GoToPosition', {
		id: getXzComponentId(device),
		pos: value,
	});
};

/** Gen2-style cover direction: `Cover.Open` / `Cover.Close` / `Cover.Stop` + `{ id }`. */
export const zwaveMultilevelSwitchDirection = (device: Device, direction: 'open' | 'close' | 'stop') => {
	const method =
		direction === 'open' ? 'Cover.Open' : direction === 'close' ? 'Cover.Close' : 'Cover.Stop';
	return xzRequest(device, method, { id: getXzComponentId(device) });
};

/** Gen2-style dimmer/light: `Light.Set` + `{ id, on?, brightness? }`. */
export const zwaveLightSet = (device: Device, params: { on?: boolean; brightness?: number }) => {
	return xzRequest(device, 'Light.Set', {
		id: getXzComponentId(device),
		...params,
	});
};

/**
 * Factory-reset / remove a Z-Wave end device on the Wave gateway.
 * Sent as Gen2 RPC to the gateway: `ZWaveDevice.FactoryReset` + `{ id: zwavedeviceComponentId }`.
 */
export const zwaveDeviceFactoryReset = async (device: Device) => {
	return zwaveDeviceGatewayRequest(device, 'ZWaveDevice.FactoryReset');
};

/** Reboot a Z-Wave end device via the gateway: `ZWaveDevice.Reboot` + `{ id }`. */
export const zwaveDeviceReboot = async (device: Device) => {
	return zwaveDeviceGatewayRequest(device, 'ZWaveDevice.Reboot');
};

/** Re-interview a Z-Wave end device via the gateway: `ZWaveDevice.Reinterview` + `{ id }`. */
export const zwaveDeviceReinterview = async (device: Device) => {
	return zwaveDeviceGatewayRequest(device, 'ZWaveDevice.Reinterview');
};

/**
 * Remove a failed Z-Wave node from the gateway network:
 * `ZWaveDevice.RemoveFailed` + `{ id }`.
 */
export const zwaveDeviceRemoveFailed = async (device: Device) => {
	return zwaveDeviceGatewayRequest(device, 'ZWaveDevice.RemoveFailed');
};

/**
 * Update Z-Wave end-device config on the gateway:
 * `ZWaveDevice.SetConfig` + `{ id, config: { name, meta } }`.
 */
export const zwaveDeviceSetConfig = async (
	device: Device,
	config: { name?: string; meta?: Record<string, unknown> },
) => {
	return zwaveDeviceGatewayRequest(device, 'ZWaveDevice.SetConfig', {
		config: {
			meta: {},
			...config,
		},
	});
};

const zwaveDeviceGatewayRequest = async (
	device: Device,
	method: string,
	params: Record<string, unknown> = {},
) => {
	const status = devices_status.getByRealId(device.id) as Record<string, any> | undefined;
	const settings = get(devices_settings)?.[real_id(device.id)];
	const id = resolveZWaveDeviceComponentId(device, status, settings);
	if (typeof id !== 'number') {
		return { isok: false, error: 'Missing Z-Wave gateway component id' } as Request;
	}

	const gateway = findZWaveGatewayForDevice(device, status);
	if (!gateway) {
		return { isok: false, error: 'Z-Wave gateway not found' } as Request;
	}

	return gen2Request(gateway, method, { id, ...params });
};
